/**
 * The Activity tab of the Production Line detail page (production-lines):
 * the line's history, newest first, projected from the summary, the intake
 * behind it, the configured line and its videos. Moved verbatim out of
 * ProductionLineDetailPage.tsx.
 */

import type { ReactNode } from 'react';

import type {
  MerchantProfileView,
  ProductionLineReadView,
} from '../../merchant/api.js';
import type { WorkspaceCopy } from '../../merchant/copy.js';
import {
  formatRelative,
  type LineSummary,
  type VideoItem,
} from '../../merchant/model.js';
import { Card, Eyebrow } from '../../merchant/ui/primitives.js';
import type { ConfiguredLine } from './productionLineJourney.js';

export function ProductionLineActivityTab({
  copy,
  language,
  summary,
  view,
  lineVideos,
  realLine,
  domain,
}: {
  readonly copy: WorkspaceCopy;
  readonly language: 'en' | 'ar';
  readonly summary: LineSummary | null;
  readonly view: MerchantProfileView | null;
  readonly lineVideos: readonly VideoItem[];
  readonly realLine: ConfiguredLine | null;
  readonly domain: string;
}): ReactNode {
  return (
    <Card role="tabpanel" className="mj-ws-mt-20" style={{ maxWidth: 720 }}>
      <Eyebrow className="mj-ws-mt-8">{copy.line.history}</Eyebrow>
      <ol className="mj-ws-act mj-ws-mt-8">
        {activityOf(summary, view, lineVideos, realLine, copy, domain).map(
          (item, index) => (
            <li key={String(index)}>
              <span className="mj-ws-muted mj-ws-sm mj-ws-tnum">
                {formatRelative(item.when, copy, language)}
              </span>
              <span className="mj-ws-sm">{item.text}</span>
            </li>
          ),
        )}
      </ol>
    </Card>
  );
}

function activityOf(
  summary: LineSummary | null,
  view: MerchantProfileView | null,
  videos: readonly VideoItem[],
  realLine: Exclude<ProductionLineReadView, { pending: true }> | null,
  copy: WorkspaceCopy,
  domain: string,
): readonly { when: string; text: string }[] {
  const items: { when: string; text: string }[] = [];
  if (summary !== null) {
    items.push({ when: summary.createdAt, text: copy.line.activity.created });
    if (domain)
      items.push({
        when: summary.createdAt,
        text: copy.line.activity.sourceRead(domain),
      });
  }
  if (view?.profile?.reviewState === 'APPROVED') {
    const first = view.analysis.progress.steps.find((step) => step.startedAt !== null);
    items.push({
      when: first?.startedAt ?? view.analysis.progress.lastUpdatedAt,
      text: copy.line.activity.profileConfirmed,
    });
  }
  for (const step of view?.analysis.progress.steps ?? []) {
    if (step.status === 'completed' && step.completedAt !== null) {
      items.push({
        when: step.completedAt,
        text: copy.line.activity.analysisStep(
          step.key === 'recommendations'
            ? copy.line.activity.analysisComplete
            : step.key,
        ),
      });
    }
  }
  if (realLine !== null) {
    items.push({
      when: realLine.productionLine.createdAt,
      text: copy.line.activity.configured(realLine.productionLine.title),
    });
  }
  for (const video of videos) {
    items.push({
      when: video.run.authorizedAt,
      text: copy.line.activity.videoStarted(video.direction || video.title),
    });
    if (video.run.confirmedAt !== null)
      items.push({
        when: video.run.confirmedAt,
        text: copy.line.activity.cardConfirmed,
      });
    if (video.run.video !== null)
      items.push({
        when: video.run.confirmedAt ?? video.run.authorizedAt,
        text: copy.line.activity.videoReady(video.title),
      });
    if (video.status === 'failed')
      items.push({ when: video.date, text: copy.line.activity.videoFailed });
  }
  return items.sort((a, b) => new Date(b.when).getTime() - new Date(a.when).getTime());
}
